import type {
  CheckoutLineFragment,
  LanguageCodeEnum,
  OrderFragment,
} from '@/graphql/saleor/api';

import { ProductTypeEnum } from '../product';
import { fetchProductsBySkus } from '../product/api';
import { checkIfVatIncluded } from '../regions';
import {
  getProductVariantStrapDetailBySkuForSaleor,
  getProductVariantWatchDetailBySkuForSaleor,
} from '../service/api';
import { formatProductSlug } from '../util';
import {
  marketEcom,
  menuCategoryMapping,
  pageTypeMapping,
  regExpMapping,
} from './const';
import type { EcommercePayload, Item } from './types';

export const getMenuCategory = (path: string) => {
  const pathSplits = path.split('?');
  if (pathSplits[0] === '/') return 'homepage';
  const menuCategory = Object.entries(menuCategoryMapping).find(([key]) =>
    `${pathSplits[0]}/`.startsWith(key),
  );
  return menuCategory ? menuCategory[1] : 'other';
};

export const getPageType = (
  path: string,
  pathname: string,
  isAuthenticated?: boolean,
) => {
  const pathSplits = path?.split('?');
  if (pathSplits[0] === '/') return 'homepage';
  if (pathSplits[0] === '/my-account/my-wishlist/' && !isAuthenticated)
    return 'wishlist';
  const pageType = pageTypeMapping[pathSplits[0]] || pageTypeMapping[pathname];
  if (pageType) return pageType;

  const _path = path.endsWith('/') ? `${path}` : `${path}/`;
  const regExpMap = Object.entries(regExpMapping).find(([, regExp]) =>
    regExp.test(_path),
  );
  if (regExpMap) return regExpMap[0];

  const pageTypeMap = Object.entries(pageTypeMapping).find(([uri]) =>
    `${pathSplits[0]}/`.startsWith(uri),
  );
  return pageTypeMap ? pageTypeMap[1] : 'other';
};

export const getMarketEcom = (country: string) => {
  return marketEcom[country.toUpperCase()] ?? 'no';
};

export const getGender = (values: string[]) => {
  let gender = 'no_gender';
  if (!values) return gender;

  if (values?.includes('men')) {
    gender = 'men';
    if (values?.includes('women')) {
      gender = 'no_gender';
    }
  } else if (values?.includes('women')) {
    gender = 'women';
  }
  return gender;
};

const strapOnlyProperties = async ({
  channel,
  languageCode,
  line,
}: {
  channel: string;
  languageCode: LanguageCodeEnum;
  line: CheckoutLineFragment;
}) => {
  const {
    variant: { product },
  } = line;

  if (product.productType.slug === 'watch') return {};

  const {
    data: { productVariant },
  } = await getProductVariantStrapDetailBySkuForSaleor(
    product.defaultVariant?.sku ?? '',
    channel,
    languageCode,
    true,
  );

  return {
    item_stock: productVariant?.quantityAvailable ? 'yes' : 'no',
  };
};

const watchOnlyProperties = async ({
  channel,
  languageCode,
  line,
}: {
  channel: string;
  languageCode: LanguageCodeEnum;
  line: CheckoutLineFragment;
}) => {
  const {
    metadata,
    variant: { product },
  } = line;

  if (product.productType.slug !== 'watch') return {};

  const {
    data: { productVariant },
  } = await getProductVariantWatchDetailBySkuForSaleor(
    product.defaultVariant?.sku as string,
    channel,
    languageCode,
    true,
  );

  const itemSubCategory = metadata.length
    ? metadata[0].value.toLowerCase()
    : 'regular';

  const item: Partial<Item> = {
    item_case_material: product?.caseMaterial?.values[0]?.translation?.name,
    item_collection: product.collections?.length
      ? product.collections[0]?.slug
      : undefined,
    item_dial_color:
      productVariant?.product.dialColor?.values[0]?.slug ?? undefined,
    item_sub_category:
      itemSubCategory === 'order' ? 'regular' : itemSubCategory,
    item_gender: getGender(
      productVariant?.product.comCategories?.values.map(
        (el) => el.slug as string,
      ) as string[],
    ),
    item_stock: productVariant?.quantityAvailable ? 'yes' : 'no',
  };

  return item;
};

const watchOrStrapProperties = ({ line }: { line: CheckoutLineFragment }) => {
  const {
    variant: { product },
  } = line;

  if (
    product.productType.slug !== 'watch' &&
    product.productType.slug !== 'strap'
  )
    return {};

  return {
    item_strap_color:
      product.strapColor?.values?.[0]?.translation?.name?.toLowerCase(),
    item_strap_material: product.strapMaterial?.values[0]?.slug ?? undefined,
    item_strap_size: product.size?.values[0]?.slug ?? undefined,
    item_sub_category: 'regular',
  };
};

export const createEcommerce = async ({
  channel,
  coupon,
  isCurrency = true,
  languageCode,
  lines,
  totalValue,
}: {
  channel: string;
  coupon?: string;
  isCurrency?: boolean;
  languageCode: LanguageCodeEnum;
  lines?: CheckoutLineFragment[];
  totalValue?: number;
}) => {
  if (!lines) return {};

  const isVatIncluded = checkIfVatIncluded(channel);
  let value = 0;
  const items: Item[] = await Promise.all(
    lines.map(async (line, index): Promise<Item> => {
      const {
        quantity,
        undiscountedUnitPrice,
        unitPrice,
        variant: { product, sku },
      } = line;

      const price = isVatIncluded
        ? unitPrice.gross.amount
        : unitPrice.net.amount;
      value += price * quantity;

      const item: Item = {
        currency: unitPrice.currency,
        index,
        price,
        quantity: quantity,
        item_name: product.name.toLowerCase(),
        item_id: (sku as string).replace('.', '_'),
        item_sub_category: 'regular',
        item_category: product.productType.slug,
        item_wishlist_compared: 'no',
        coupon: coupon,
        discount: undiscountedUnitPrice.amount - price,
        ...watchOrStrapProperties({ line }),
        ...(await watchOnlyProperties({
          channel,
          languageCode,
          line,
        })),
        ...(await strapOnlyProperties({
          channel,
          languageCode,
          line,
        })),
      };

      return item;
    }),
  );

  const ecommerce: EcommercePayload = {
    coupon,
    value: +(totalValue ?? value).toFixed(2),
    items,
  };

  if (items.length > 0 && isCurrency) ecommerce.currency = items[0].currency;

  return ecommerce;
};

export const extractCheckoutLinesFromOrder = (
  order: OrderFragment,
): CheckoutLineFragment[] | undefined => {
  const {
    lines,
    orderType,
    totalCharged: { amount: chargedAmount },
  } = order;

  if (orderType === 'PRE_ORDER' && chargedAmount > 0) return undefined;

  const isVatIncluded = checkIfVatIncluded(order.channel.slug);

  return lines.map((el) => ({
    id: el.id,
    unitPrice: el.unitPrice,
    undiscountedUnitPrice: isVatIncluded
      ? el.undiscountedUnitPrice.gross
      : el.undiscountedUnitPrice.net,
    totalPrice: el.unitPrice,
    undiscountedTotalPrice: {
      amount: 3500,
      currency: 'CHF',
    },
    variant: el.variant as never,
    strapSize: el.strapSize,
    quantity: 1,
    metadata: [],
  }));
};

export const createEcommerceBySkus = async ({
  channel,
  itemListName,
  languageCode,
  skus,
}: {
  channel: string;
  itemListName?: string;
  languageCode: LanguageCodeEnum;
  skus: string[];
}) => {
  const slugs = skus
    .filter((sku) => !!sku && typeof sku === 'string')
    .map(formatProductSlug);

  if (slugs.length === 0) return { items: [] };

  const data = await fetchProductsBySkus({
    skus: slugs,
    languageCode: languageCode,
  });

  const isVatIncluded = checkIfVatIncluded(channel);

  const productItems =
    data?.products?.edges.map(({ node: product }) => {
      const listing = product.channelListings?.find(
        (listing) =>
          listing.channel.name.toLowerCase() === channel.toLowerCase(),
      );
      const pricing = listing?.pricing?.priceRange?.start;
      const price = isVatIncluded ? pricing?.gross.amount : pricing?.net.amount;
      switch (product.productType.slug) {
        case ProductTypeEnum.Watch:
          return {
            item_id: product.slug,
            item_name: product.name.toLocaleLowerCase(),
            item_category: product.productType.slug,
            item_sub_category: 'regular',
            item_collection: product.collections?.length
              ? product.collections[0]?.slug
              : undefined,
            item_list_name: itemListName,
            currency: pricing?.gross.currency,
            item_dial_color: product.dialColor?.values[0]?.slug ?? undefined,
            item_case_material:
              product?.caseMaterial?.values[0]?.translation?.name,
            price,
            quantity: 1,
            item_stock: product.defaultVariant?.quantityAvailable
              ? 'yes'
              : 'no',
            item_strap_material:
              product.strapMaterial?.values[0]?.slug ?? undefined,
            item_strap_color:
              product.strapColor?.values?.[0]?.translation?.name?.toLowerCase(),
            item_strap_size: undefined,
            item_wishlist_compared: 'no',
            item_gender: getGender(
              product.comCategories?.values.map(
                (el) => el.slug as string,
              ) as string[],
            ),
            coupon: undefined,
            discount: 0,
          };
        case ProductTypeEnum.Strap:
          return {
            item_id: product.slug,
            item_name: product.name.toLocaleLowerCase(),
            item_category: product.productType.slug,
            item_sub_category: 'regular',
            item_list_name: itemListName,
            currency: pricing?.gross.currency,
            price,
            quantity: 1,
            item_stock: product.defaultVariant?.quantityAvailable
              ? 'yes'
              : 'no',
            item_strap_material:
              product.strapMaterial?.values[0]?.slug ?? undefined,
            item_strap_size: undefined,
            item_wishlist_compared: 'no',
            item_strap_color:
              product.strapColor?.values?.[0]?.translation?.name?.toLowerCase(),
            coupon: undefined,
            discount: 0,
          };
        default:
          return {
            item_id: product.slug,
            item_name: product.name.toLocaleLowerCase(),
            item_category: product.productType.slug,
            item_sub_category: 'regular',
            item_list_name: itemListName,
            currency: pricing?.gross.currency,
            price,
            quantity: 1,
            item_stock: product.defaultVariant?.quantityAvailable
              ? 'yes'
              : 'no',
            item_wishlist_compared: 'no',
            coupon: undefined,
            discount: 0,
          };
      }
    }) ?? [];

  const items = (skus.map((sku, i) => {
    const item = productItems.find(
      ({ item_id }) => item_id === formatProductSlug(sku),
    );
    return { ...item, index: i };
  }) ?? []) as Item[];

  return {
    items,
  };
};
